import React, { useEffect, useMemo } from "react";
import { ReactComponent as CircleIcon } from "./../../images/svg/circle.svg";
import { ReactComponent as ArrowLeftIcon } from "./../../images/svg/arrowLeft.svg";
import { gettext } from "../../adapter";
import SensorDropdown from "./SensorDropdown";
import Information from "./Information";
import Map from "./Map";
import LoadError from "../LoadError";
import NProgress from "../../libraries/progress";
import Loader from "../Loader";
import IrrigationCharts from "./IrrigationCharts";
import { cropr } from "../../adapter";
import { parseSensor } from "../../utils/sensorUtils";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchSensor } from "../../actions/sensorActions";
import { useParams, useLocation } from "react-router-dom";

const SensorDetail = ({ basePath, apiUrl }) => {
  const sensorState = useSelector((state) => {
    return state.sensor;
  });

  const { sensor, loadError, isFetching, lastDataPoint, domain, defaultDomain } = useMemo(() => {
    return {
      ...sensorState,
      ...parseSensor(sensorState.sensor, sensorState.advice),
    };
  }, [sensorState]);

  const { sensorId } = useParams();
  const dispatch = useDispatch();
  const { search } = useLocation();

  useEffect(() => {
    NProgress.start();
    dispatch(fetchSensor(apiUrl, sensorId, null, true, "&days=60", false));
  }, [sensorId, dispatch, apiUrl]);

  if (!sensor) {
    if (loadError) {
      NProgress.done();
      return <LoadError />;
    } else {
      return <Loader message={gettext("Loading sensor data...")} />;
    }
  }

  const onClick = () => {
    NProgress.start();
    dispatch(fetchSensor(apiUrl, sensorId, null, true, "", false));
  };

  if (!isFetching) {
    NProgress.done();
  }

  var iconClass = "icon is-uknown";
  var stateText = gettext("Unknown");
  if (sensor.status === "new") {
    iconClass = "icon is-small text-gray";
    stateText = gettext("{{type}} is not yet configured").replace("{{type}}", sensor.type);
  } else if (sensor.active === undefined && sensor.enabled === undefined) {
    iconClass = "icon is-small text-gray";
    stateText = gettext("Unknown");
  } else if (sensor.active && sensor.enabled) {
    iconClass = "icon is-small text-green";
    stateText = gettext("{{type}} is switched on").replace("{{type}}", sensor.type);
  } else if (!sensor.enabled && sensor.active) {
    iconClass = "icon is-small text-yellow";
    stateText = gettext(
      "{{type}} is being switched off but is currently still sending data."
    ).replace("{{type}}", sensor.type);
  } else if (sensor.enabled && !sensor.active) {
    iconClass = "icon is-small text-yellow";
    stateText = gettext("{{type}} is switched on but sends currently no data.").replace(
      "{{type}}",
      sensor.type
    );
  } else if (!sensor.enabled && !sensor.active) {
    iconClass = "icon is-small text-red";
    stateText = gettext("{{type}} is switched off").replace("{{type}}", sensor.type);
  }

  var editSensor = "";
  if (
    (sensor.role === "owner" || sensor.role === "editor") &&
    cropr.permissions.farm.update === true
  ) {
    var editHref = basePath + "/" + sensor.id + "/edit" + search;
    editSensor = (
      <a href={editHref} className="button is-right">
        {gettext("Edit sensor")}
      </a>
    );
  }

  var message = "";
  if (moment().isBefore(sensor.startDate)) {
    message = (
      <div className="l-full">
        <div className="panel--light has-small-innner-spacing">
          {gettext(
            "The start date of the measurement period is set to a future date. Please modify the start date in order to show recent measurements."
          )}
        </div>
      </div>
    );
  }
  if (moment().isAfter(sensor.endDate)) {
    message = (
      <div className="l-full">
        <div className="panel--light has-small-innner-spacing">
          {gettext(
            "The end date of the measurement period is set to a date in the past. Please modify the end date in order to show recent measurements."
          )}
        </div>
      </div>
    );
  }
  var message2 = "";
  if (sensor.longitude === 0.0 && sensor.latitude === 0.0) {
    message2 = (
      <div className="l-full text-red">
        <div className="panel--light has-small-innner-spacing">
          {gettext("This sensor")} {gettext("has no valid position")}
        </div>
      </div>
    );
  }

  var charts = "";
  // Only show charts if we have actual data
  if (lastDataPoint && sensor.startDate) {
    charts = (
      <IrrigationCharts
        sensor={sensor}
        apiUrl={apiUrl}
        lastDataPoint={lastDataPoint}
        domain={domain ? domain : defaultDomain}
        loading={isFetching}
      />
    );
  }

  return (
    <div>
      <div className="l-full buttons--header">
        <SensorDropdown sensor={sensor} />
        <h1>
          &nbsp; <CircleIcon className={iconClass} />
        </h1>
        <div className="buttons is-inline">
          <a href={`${basePath}/${search}`} className="button--normal">
            <ArrowLeftIcon />
            {gettext("Back to overview")}
          </a>
          {/* eslint-disable-next-line */}
          <a className="button--normal" onClick={onClick}>
            {gettext("Show entire period")}
          </a>
          {editSensor}
        </div>
      </div>
      {message}
      {message2}
      {charts}
      <div className="dacom-equal-row">
        <div className="l-half">
          <div className="panel dacom-equal-cell">
            <Information
              sensor={sensor}
              lastDataPoint={lastDataPoint}
              iconClass={iconClass}
              stateText={stateText}
              view={sensor.view}
            />
          </div>
        </div>
        <div className="l-half">
          <div className="panel dacom-equal-cell">
            <h2>{gettext("Location")}</h2>
            <Map className="map middle" sensor={sensor} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensorDetail;
